import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Download,
  Printer,
  BookOpen,
  Grid,
  Maximize2
} from 'lucide-react';

export default function BookView({ layout, photos = [], coverUrl }) {
  const [currentPage, setCurrentPage] = useState(0);
  const [viewMode, setViewMode] = useState('spread'); // 'spread', 'single', 'grid'
  const [zoom, setZoom] = useState(1);

  const pages = layout?.pages || [];
  const totalPages = pages.length;

  const getPhotosForPage = (page) => {
    if (!page?.photo_ids) return [];
    return page.photo_ids
      .map(id => photos.find(p => p.id === id))
      .filter(Boolean);
  };

  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowLeft':
        setCurrentPage(prev => Math.max(0, prev - (viewMode === 'spread' ? 2 : 1)));
        break;
      case 'ArrowRight':
        setCurrentPage(prev => Math.min(totalPages - 1, prev + (viewMode === 'spread' ? 2 : 1)));
        break;
      case '+':
        setZoom(prev => Math.min(2, prev + 0.25));
        break;
      case '-':
        setZoom(prev => Math.max(0.5, prev - 0.25));
        break;
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewMode, totalPages]);

  const renderPage = (pageIndex, side = 'left') => {
    if (pageIndex < 0 || pageIndex >= totalPages) {
      return (
        <div className="w-full h-full bg-muted/20 flex items-center justify-center">
          <span className="text-muted-foreground text-sm">
            {pageIndex < 0 ? '' : 'Koniec albumu'}
          </span>
        </div>
      );
    }

    const page = pages[pageIndex];
    const pagePhotos = getPhotosForPage(page);
    const photoCount = pagePhotos.length;

    // Determine layout based on photo count
    let gridClass = 'grid-cols-1';
    if (photoCount === 2) gridClass = 'grid-cols-2';
    if (photoCount === 3) gridClass = 'grid-cols-2';
    if (photoCount >= 4) gridClass = 'grid-cols-2';

    return (
      <div
        className={`w-full h-full bg-white p-4 ${
          side === 'left' ? 'rounded-l-sm' : 'rounded-r-sm'
        }`}
        style={{
          boxShadow: side === 'left'
            ? 'inset -10px 0 20px rgba(0,0,0,0.1)'
            : 'inset 10px 0 20px rgba(0,0,0,0.1)'
        }}
      >
        <div className={`grid ${gridClass} gap-2 h-full`}>
          {pagePhotos.map((photo, idx) => (
            <div
              key={photo.id}
              className={`relative overflow-hidden rounded ${
                photoCount === 3 && idx === 0 ? 'row-span-2' : ''
              }`}
            >
              <img
                src={photo.url || photo.thumbnail_url}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>

        {/* Page Number */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs text-gray-400">
          {pageIndex + 1}
        </div>

        {/* Narrative */}
        {page?.narrative && (
          <div className="absolute bottom-8 left-4 right-4 text-center text-sm text-gray-600 italic">
            {page.narrative}
          </div>
        )}
      </div>
    );
  };

  if (!layout || pages.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-12 text-center">
          <BookOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-medium mb-2">Brak wygenerowanego layoutu</h3>
          <p className="text-sm text-muted-foreground">
            Uzyj Magic Designer, aby wygenerowac layout albumu
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between p-4 bg-card border border-border rounded-lg">
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'spread' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('spread')}
          >
            <BookOpen className="w-4 h-4 mr-1" />
            Rozkladowka
          </Button>
          <Button
            variant={viewMode === 'single' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('single')}
          >
            <Maximize2 className="w-4 h-4 mr-1" />
            Pojedyncza
          </Button>
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            <Grid className="w-4 h-4 mr-1" />
            Siatka
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setZoom(prev => Math.max(0.5, prev - 0.25))}
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-sm w-16 text-center">{Math.round(zoom * 100)}%</span>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setZoom(prev => Math.min(2, prev + 0.25))}
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Printer className="w-4 h-4 mr-1" />
            Drukuj
          </Button>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-1" />
            Eksportuj PDF
          </Button>
        </div>
      </div>

      {/* Book View */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-4 p-4">
          {pages.map((page, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.02 }}
              className="aspect-[3/4] bg-white rounded shadow cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => {
                setCurrentPage(idx);
                setViewMode('single');
              }}
            >
              <div className="w-full h-full p-1">
                {getPhotosForPage(page).slice(0, 1).map((photo) => (
                  <img
                    key={photo.id}
                    src={photo.thumbnail_url || photo.url}
                    alt=""
                    className="w-full h-full object-cover rounded"
                  />
                ))}
              </div>
              <div className="text-center text-xs text-muted-foreground -mt-1">
                {idx + 1}
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="relative bg-gray-900 rounded-lg overflow-hidden" style={{ height: '70vh' }}>
          {/* Navigation Buttons */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white"
            onClick={() => setCurrentPage(prev => Math.max(0, prev - (viewMode === 'spread' ? 2 : 1)))}
            disabled={currentPage === 0}
          >
            <ChevronLeft className="w-8 h-8" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white"
            onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + (viewMode === 'spread' ? 2 : 1)))}
            disabled={currentPage >= totalPages - 1}
          >
            <ChevronRight className="w-8 h-8" />
          </Button>

          {/* Book */}
          <div
            className="absolute inset-0 flex items-center justify-center p-8"
            style={{ transform: `scale(${zoom})` }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentPage}
                initial={{ opacity: 0, rotateY: -10 }}
                animate={{ opacity: 1, rotateY: 0 }}
                exit={{ opacity: 0, rotateY: 10 }}
                transition={{ duration: 0.3 }}
                className={`flex shadow-2xl ${viewMode === 'spread' ? 'gap-0' : ''}`}
                style={{
                  perspective: '1000px',
                  height: viewMode === 'spread' ? '80%' : '90%',
                  aspectRatio: viewMode === 'spread' ? '2/1.4' : '1/1.4',
                }}
              >
                {viewMode === 'spread' ? (
                  <>
                    <div className="w-1/2 h-full relative">
                      {renderPage(currentPage, 'left')}
                    </div>
                    <div className="w-1/2 h-full relative">
                      {renderPage(currentPage + 1, 'right')}
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full relative">
                    {renderPage(currentPage, 'left')}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Page Counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 px-4 py-2 rounded-full text-white text-sm">
            {viewMode === 'spread'
              ? `${currentPage + 1}-${Math.min(currentPage + 2, totalPages)} / ${totalPages}`
              : `${currentPage + 1} / ${totalPages}`}
          </div>
        </div>
      )}

      {/* Cover Preview */}
      {coverUrl && (
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <h4 className="text-sm font-medium mb-2">Okladka albumu:</h4>
            <img
              src={coverUrl}
              alt="Album cover"
              className="max-w-xs rounded shadow"
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
